import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Switch } from './ui/switch';
import { Plus, Trash2, Save, Edit2 } from 'lucide-react';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useCapabilities } from '@/context/CapabilitiesContext';
import { useDialog } from '@/context/DialogContext';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
export default function LeaveSettingsTab() {
  const { confirm } = useDialog();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [leaveAdditionTiming, setLeaveAdditionTiming] = useState('calendar_year');
  const [fiscalYearStartMonth, setFiscalYearStartMonth] = useState(4);
  const [fiscalYearStartDay, setFiscalYearStartDay] = useState(1);
  const [leaveTypes, setLeaveTypes] = useState([]);

  const MONTHS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/leave-settings');
      const data = res.data || {};
      setLeaveAdditionTiming(data.leave_addition_timing || 'calendar_year');
      setFiscalYearStartMonth(data.fiscal_year_start_month || 4);
      setFiscalYearStartDay(data.fiscal_year_start_day || 1);
      setLeaveTypes(data.leave_types || []);
    } catch (err) {
      toast.error('Failed to load leave settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/leave-settings', {
        leave_addition_timing: leaveAdditionTiming,
        fiscal_year_start_month: fiscalYearStartMonth,
        fiscal_year_start_day: fiscalYearStartDay,
        leave_types: leaveTypes.map((lt, idx) => ({ ...lt, order: idx })),
      });
      toast.success('Leave settings saved');
      fetchSettings();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to save settings';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addLeaveType = () => {
    setLeaveTypes([...leaveTypes, {
      type_key: `leave_${Date.now()}`,
      type_label: '',
      max_days_per_year: 10,
      color: '#002FA7',
      allow_carry_over: true,
      max_carry_over_days: 5,
      carry_over_expiry_months: 3,
      no_consecutive_days: false
    }]);
  };

  const updateLeaveType = (index, updates) => {
    const updated = [...leaveTypes];
    updated[index] = { ...updated[index], ...updates };
    setLeaveTypes(updated);
  };

  const removeLeaveType = async (index) => {
    const ok = await confirm({
      title: "Remove this leave type?",
      description: "Employees will no longer be able to select this leave type in new requests.",
      confirmText: "Remove",
      variant: "destructive",
    });
    if (ok) {
      setLeaveTypes(leaveTypes.filter((_, i) => i !== index));
    }
  };

  const moveLeaveType = (index, direction) => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === leaveTypes.length - 1)) return;
    const newTypes = [...leaveTypes];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newTypes[index], newTypes[targetIndex]] = [newTypes[targetIndex], newTypes[index]];
    setLeaveTypes(newTypes);
  };

  // Leave Templates state and functions
  const { can } = useCapabilities();
  const canManageTemplates = can('create_leave_templates');
  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

  const emptyTemplate = {
    name: '', description: '', default_days: 1, leave_type: 'casual',
    allowed_types: ['casual'], min_notice_days: 0, max_days: null,
    reason_required: false, reason_placeholder: '',
    attachment_required: false, half_day_allowed: true,
    approval_chain: [], color: '#002FA7', icon: '',
  };

  const loadTemplates = async () => {
    try {
      const res = await api.get('/leave-templates');
      setTemplates(res.data || []);
    } catch (err) {
      console.error('Failed to load templates');
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const openNewTemplate = () => {
    setEditingTemplate({ ...emptyTemplate });
    setTemplateDialogOpen(true);
  };

  const openEditTemplate = (t) => {
    setEditingTemplate({
      ...emptyTemplate,
      ...t,
      allowed_types: t.allowed_types || [t.leave_type],
      approval_chain: t.approval_chain || [],
    });
    setTemplateDialogOpen(true);
  };

  const deleteTemplate = async (id) => {
    const ok = await confirm({
      title: "Delete this leave template?",
      description: "The template will no longer be available for future leave applications.",
      confirmText: "Delete template",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/leave-templates/${id}`);
      toast.success('Template deleted');
      loadTemplates();
    } catch (err) {
      toast.error('Failed to delete template');
    }
  };

  const saveTemplate = async () => {
    try {
      const body = { ...editingTemplate };
      body.default_days = parseInt(body.default_days) || 1;
      body.min_notice_days = parseInt(body.min_notice_days) || 0;
      body.max_days = body.max_days ? parseInt(body.max_days) : null;
      
      if (editingTemplate.id) {
        await api.patch(`/leave-templates/${editingTemplate.id}`, body);
        toast.success('Template updated');
      } else {
        await api.post('/leave-templates', body);
        toast.success('Template created');
      }
      setTemplateDialogOpen(false);
      setEditingTemplate(null);
      loadTemplates();
    } catch (err) {
      toast.error('Failed to save template');
    }
  };

  const toggleType = (t) => {
    const arr = editingTemplate.allowed_types.includes(t)
      ? editingTemplate.allowed_types.filter(x => x !== t)
      : [...editingTemplate.allowed_types, t];
    setEditingTemplate({ ...editingTemplate, allowed_types: arr.length ? arr : [t] });
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Leave Management</h3>
          <p className="text-sm text-muted-foreground">Configure leave types, templates, rules, and allocation timing</p>
        </div>
      </div>

      <Tabs defaultValue="types" className="w-full">
        <TabsList>
          <TabsTrigger value="types">Leave Types & Rules</TabsTrigger>
          <TabsTrigger value="templates">Leave Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="types" className="space-y-6 mt-6">
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>

          {/* Leave Addition Timing */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Leave Allocation Timing</CardTitle>
              <CardDescription>When should new leave balances be added?</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Timing Mode</Label>
                <Select value={leaveAdditionTiming} onValueChange={setLeaveAdditionTiming}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="calendar_year">Calendar Year (January 1)</SelectItem>
                    <SelectItem value="fiscal_year">Fiscal Year (Custom)</SelectItem>
                    <SelectItem value="anniversary">Employee Anniversary</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {leaveAdditionTiming === 'fiscal_year' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Fiscal Year Start Month</Label>
                    <Select value={String(fiscalYearStartMonth)} onValueChange={(val) => setFiscalYearStartMonth(parseInt(val))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTHS.map(m => (
                          <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Start Day</Label>
                    <Input type="number" min="1" max="31" value={fiscalYearStartDay} onChange={(e) => setFiscalYearStartDay(parseInt(e.target.value) || 1)} />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Leave Types Configuration */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-base">Leave Types</CardTitle>
                  <CardDescription>Configure leave types, limits, and rules</CardDescription>
                </div>
                <Button size="sm" onClick={addLeaveType}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Type
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {leaveTypes.map((lt, idx) => (
                <Card key={idx} className="border-l-4" style={{ borderLeftColor: lt.color }}>
                  <CardContent className="pt-4 space-y-4">
                    <div className="flex items-start gap-2">
                      <div className="flex flex-col gap-1 mt-2">
                        <Button size="sm" variant="ghost" onClick={() => moveLeaveType(idx, 'up')} disabled={idx === 0}>
                          ↑
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => moveLeaveType(idx, 'down')} disabled={idx === leaveTypes.length - 1}>
                          ↓
                        </Button>
                      </div>
                      
                      <div className="flex-1 space-y-4">
                        {/* Basic Info */}
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <Label>Type Key</Label>
                            <Input value={lt.type_key} onChange={(e) => updateLeaveType(idx, { type_key: e.target.value })} placeholder="casual" />
                          </div>
                          <div>
                            <Label>Display Label</Label>
                            <Input value={lt.type_label} onChange={(e) => updateLeaveType(idx, { type_label: e.target.value })} placeholder="Casual Leave" />
                          </div>
                          <div>
                            <Label>Color</Label>
                            <Input type="color" value={lt.color} onChange={(e) => updateLeaveType(idx, { color: e.target.value })} />
                          </div>
                        </div>

                        <Separator />

                        {/* Days Configuration */}
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <Label>Max Days/Year</Label>
                            <Input type="number" min="0" max="365" value={lt.max_days_per_year} onChange={(e) => updateLeaveType(idx, { max_days_per_year: parseInt(e.target.value) || 0 })} />
                          </div>
                          <div>
                            <Label>Max Carry Over</Label>
                            <Input 
                              type="number" 
                              min="0" 
                              max="365" 
                              value={lt.max_carry_over_days || ''} 
                              onChange={(e) => updateLeaveType(idx, { max_carry_over_days: e.target.value ? parseInt(e.target.value) : null })} 
                              placeholder="Unlimited"
                              disabled={!lt.allow_carry_over}
                            />
                          </div>
                          <div>
                            <Label>Carry Over Expiry (Months)</Label>
                            <Input 
                              type="number" 
                              min="1" 
                              max="24" 
                              value={lt.carry_over_expiry_months || ''} 
                              onChange={(e) => updateLeaveType(idx, { carry_over_expiry_months: e.target.value ? parseInt(e.target.value) : null })} 
                              placeholder="Never expires"
                              disabled={!lt.allow_carry_over}
                            />
                          </div>
                        </div>

                        <Separator />

                        {/* Rules */}
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <Switch checked={lt.allow_carry_over} onCheckedChange={(val) => updateLeaveType(idx, { allow_carry_over: val })} />
                            <Label>Allow carry over to next year</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch checked={lt.no_consecutive_days} onCheckedChange={(val) => updateLeaveType(idx, { no_consecutive_days: val })} />
                            <Label>Block consecutive day applications</Label>
                          </div>
                        </div>
                      </div>

                      <Button size="sm" variant="ghost" onClick={() => removeLeaveType(idx)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {leaveTypes.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No leave types configured. Add one to get started.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates" className="space-y-6 mt-6">
          <div className="flex justify-end">
            {canManageTemplates && (
              <Button onClick={openNewTemplate}>
                <Plus className="h-4 w-4 mr-2" />
                New Template
              </Button>
            )}
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Leave Application Templates</CardTitle>
              <CardDescription>Define templates with specific fields and approval flows for different leave types</CardDescription>
            </CardHeader>
            <CardContent>
              {templates.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No templates created yet.
                </div>
              ) : (
                <div className="space-y-3">
                  {templates.map(t => (
                    <div key={t.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded" style={{ backgroundColor: t.color || '#002FA7' }} />
                        <span className="text-lg">{t.icon}</span>
                        <div>
                          <div className="font-semibold">{t.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {t.description} · {t.allowed_types?.join(', ')} · Min notice: {t.min_notice_days} days
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => openEditTemplate(t)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteTemplate(t.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Template Edit Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate?.id ? 'Edit' : 'Create'} Leave Template</DialogTitle>
            <DialogDescription>Configure template fields and rules</DialogDescription>
          </DialogHeader>

          {editingTemplate && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Template Name</Label>
                  <Input value={editingTemplate.name} onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })} placeholder="e.g., Medical Leave" />
                </div>
                <div>
                  <Label>Icon (emoji)</Label>
                  <Input value={editingTemplate.icon} onChange={(e) => setEditingTemplate({ ...editingTemplate, icon: e.target.value })} placeholder="🏥" />
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Textarea value={editingTemplate.description} onChange={(e) => setEditingTemplate({ ...editingTemplate, description: e.target.value })} rows={2} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Default Days</Label>
                  <Input type="number" min="1" value={editingTemplate.default_days} onChange={(e) => setEditingTemplate({ ...editingTemplate, default_days: parseInt(e.target.value) || 1 })} />
                </div>
                <div>
                  <Label>Min Notice Days</Label>
                  <Input type="number" min="0" value={editingTemplate.min_notice_days} onChange={(e) => setEditingTemplate({ ...editingTemplate, min_notice_days: parseInt(e.target.value) || 0 })} />
                </div>
              </div>

              <div>
                <Label>Allowed Leave Types</Label>
                <div className="flex gap-2 flex-wrap mt-2">
                  {['casual', 'sick', 'annual', 'unpaid'].map(type => (
                    <Button
                      key={type}
                      size="sm"
                      variant={editingTemplate.allowed_types?.includes(type) ? 'default' : 'outline'}
                      onClick={() => toggleType(type)}
                    >
                      {type}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Switch checked={editingTemplate.reason_required} onCheckedChange={(val) => setEditingTemplate({ ...editingTemplate, reason_required: val })} />
                  <Label>Reason required</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={editingTemplate.attachment_required} onCheckedChange={(val) => setEditingTemplate({ ...editingTemplate, attachment_required: val })} />
                  <Label>Attachment required</Label>
                </div>
              </div>

              <div>
                <Label>Color</Label>
                <Input type="color" value={editingTemplate.color} onChange={(e) => setEditingTemplate({ ...editingTemplate, color: e.target.value })} />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveTemplate}>Save Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
